import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import CaseMessages from "@/components/CaseMessages";
import { SignFormDialog } from "@/components/SignFormDialog";
import { ComplianceChecklist } from "@/components/ComplianceChecklist";
import { SalaryTool } from "@/components/SalaryTool";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, Loader2, CheckCircle2, ArrowLeft, Download, ClipboardList, PenLine } from "lucide-react";

const REQ_BADGE = {
  requested: { cls: "bg-[#FEF3C7] text-[#92600A]", label: "Needed" },
  uploaded: { cls: "bg-[#DBEAFE] text-[#1E40AF]", label: "Uploaded" },
  approved: { cls: "bg-[#DCFCE7] text-[#166534]", label: "Approved" },
  rejected: { cls: "bg-[#FEE2E2] text-[#B91C1C]", label: "Re-upload" },
};

const FIELDS = [
  { name: "business_name", label: "Legal business name", placeholder: "Acme Holdings LLC", required: true },
  { name: "entity_state", label: "State of formation", placeholder: "Delaware", required: true },
  { name: "ein", label: "EIN (optional)", placeholder: "12-3456789", required: false },
  { name: "num_owners", label: "Number of owners/members", placeholder: "1", required: true },
  { name: "annual_revenue", label: "Approx. annual net profit", placeholder: "$120,000", required: true },
  { name: "current_payroll", label: "Do you currently run payroll?", placeholder: "No, not yet", required: true },
];

export default function Intake() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    business_name: "", entity_state: "", ein: "", num_owners: "",
    annual_revenue: "", current_payroll: "", notes: "",
  });
  const [intake, setIntake] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [docType, setDocType] = useState("other");
  const [signOpen, setSignOpen] = useState(false);
  const [signedForms, setSignedForms] = useState([]);

  const loadSigned = () => api.get("/intake/signed-forms").then(({ data }) => setSignedForms(data)).catch(() => {});

  useEffect(() => {
    api.get("/doc-types").then(({ data }) => setDocTypes(data)).catch(() => {});
    api.get("/intake/me").then(({ data }) => {
      if (data && data.case_id) {
        setIntake(data);
        setForm({
          business_name: data.business_name || "", entity_state: data.entity_state || "",
          ein: data.ein || "", num_owners: data.num_owners || "",
          annual_revenue: data.annual_revenue || "", current_payroll: data.current_payroll || "",
          notes: data.notes || "",
        });
        loadSigned();
      }
    }).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const downloadDelivered = async (storagePath, filename, fileId) => {
    try {
      const res = await api.get(`/files/${storagePath}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      if (fileId) {
        api.post(`/intake/delivered/${fileId}/seen`).catch(() => {});
        setIntake((p) => p && ({
          ...p,
          delivered_files: (p.delivered_files || []).map((f) => f.file_id === fileId ? { ...f, seen_by_client: true } : f),
        }));
      }
    } catch {
      toast.error("Could not download document");
    }
  };

  const saveIntake = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/intake", form);
      setIntake(data);
      toast.success("Intake saved", { description: "You can now upload supporting documents." });
    } catch {
      toast.error("Could not save intake. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!intake) {
      toast.error("Please save your intake details first.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post("/intake/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/intake/me");
      setIntake(data);
      toast.success("Document uploaded");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadForRequest = (requestId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.png,.jpg,.jpeg,.doc,.docx";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("request_id", requestId);
      setUploading(true);
      try {
        await api.post("/intake/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const { data } = await api.get("/intake/me");
        setIntake(data);
        toast.success("Document uploaded");
      } catch {
        toast.error("Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm text-[#595D5A] hover:text-[#114A31]"
          data-testid="back-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <h1 className="mt-4 font-heading text-3xl tracking-tight font-medium text-[#1C1C1C]">
          Full-Service Intake
        </h1>
        <p className="mt-2 text-[#595D5A]">
          Tell us about your business and upload your documents. Our specialists handle the filing,
          payroll, and tax account setup from here.
        </p>

        <form onSubmit={saveIntake} className="mt-8 space-y-5" data-testid="intake-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.name}>
                <Label htmlFor={f.name} className="text-[#1C1C1C]">{f.label}</Label>
                <Input
                  id={f.name} name={f.name} value={form[f.name]} onChange={handleChange}
                  required={f.required} placeholder={f.placeholder}
                  data-testid={`intake-${f.name}`}
                  className="mt-1.5 rounded-xl border-[#E5E5E5] py-6 bg-white"
                />
              </div>
            ))}
          </div>
          <div>
            <Label htmlFor="notes" className="text-[#1C1C1C]">Anything else we should know?</Label>
            <Textarea
              id="notes" name="notes" value={form.notes} onChange={handleChange}
              data-testid="intake-notes"
              className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white min-h-[100px]"
              placeholder="Existing accountant, prior elections, deadlines…"
            />
          </div>
          <Button
            type="submit" disabled={saving} data-testid="intake-submit-btn"
            className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-8 py-6"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : intake ? "Save changes" : "Save intake"}
          </Button>
        </form>

        {/* Upload */}
        <div className="mt-10 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="upload-section">
          <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Supporting documents</h2>
          <p className="text-sm text-[#595D5A] mt-1">
            Upload your operating agreement, prior tax returns, or EIN letter (PDF, PNG, JPG).
          </p>

          <input
            ref={fileRef} type="file" className="hidden" onChange={handleUpload}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="file-input"
          />
          <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="sm:w-64">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-xl border-[#E5E5E5] bg-white" data-testid="doc-type-select"><SelectValue placeholder="Document type" /></SelectTrigger>
                <SelectContent>
                  {docTypes.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !intake}
              variant="outline"
              data-testid="upload-btn"
              className="rounded-full border-[#E5E5E5] hover:border-[#114A31] hover:bg-[#F9F9F8] text-[#1C1C1C]"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? "Uploading…" : "Upload document"}
            </Button>
          </div>
          {!intake && (
            <p className="mt-2 text-xs text-[#F59E0B]">Save your intake details to enable uploads.</p>
          )}

          {intake?.files?.length > 0 && (
            <ul className="mt-5 space-y-2" data-testid="file-list">
              {intake.files.map((f) => (
                <li key={f.file_id} className="flex items-center gap-3 text-sm bg-[#F9F9F8] rounded-xl px-4 py-3">
                  <FileText className="h-4 w-4 text-[#114A31]" />
                  <span className="flex-1 text-[#1C1C1C] truncate">{f.original_filename}</span>
                  {f.doc_label && <span className="text-[10px] uppercase tracking-wide bg-[#E6F0EB] text-[#114A31] px-2 py-0.5 rounded-full shrink-0">{f.doc_label}</span>}
                  <CheckCircle2 className="h-4 w-4 text-[#22C55E]" />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* E-signature */}
        {intake?.case_id && (
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="esign-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <PenLine className="h-5 w-5 text-[#114A31]" />
                <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Sign your IRS forms</h2>
              </div>
              <Button onClick={() => setSignOpen(true)} data-testid="open-sign-btn"
                className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
                <PenLine className="h-4 w-4 mr-2" /> Sign a form
              </Button>
            </div>
            <p className="text-sm text-[#595D5A] mt-1">
              Sign Form 2553 (with every owner's consent), engagement letters, and meeting minutes right here — no printing required.
            </p>
            {signedForms.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="signed-forms-list">
                {signedForms.map((f) => (
                  <li key={f.file_id} className="flex items-center gap-3 text-sm bg-[#E6F0EB] rounded-xl px-4 py-3">
                    <FileText className="h-4 w-4 text-[#114A31]" />
                    <span className="flex-1 text-[#1C1C1C] truncate">{f.doc_label || f.original_filename}</span>
                    <span className="text-xs text-[#595D5A]">{f.signer_count} signer{f.signer_count === 1 ? "" : "s"}</span>
                    <button onClick={() => downloadDelivered(f.storage_path, f.original_filename)}
                      data-testid={`download-signed-${f.file_id}`} className="text-[#114A31] hover:text-[#1A6B46]"><Download className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Compliance checklist */}
        {intake?.case_id && (
          <div className="mt-6">
            <ComplianceChecklist scope="client" basePath="/intake/compliance" testId="client-compliance" />
          </div>
        )}

        {/* Reasonable Salary Analyzer */}
        {intake?.case_id && (
          <div className="mt-6">
            <SalaryTool />
          </div>
        )}

        {/* Requested documents checklist */}
        {intake?.doc_requests?.length > 0 && (
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="doc-checklist">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#114A31]" />
              <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Documents your specialist needs</h2>
            </div>
            <p className="text-sm text-[#595D5A] mt-1">Upload each requested item below. Your specialist reviews and approves them.</p>
            <ul className="mt-4 space-y-2">
              {intake.doc_requests.map((r) => {
                const badge = REQ_BADGE[r.status] || REQ_BADGE.requested;
                const done = r.status === "uploaded" || r.status === "approved";
                return (
                  <li key={r.id} data-testid={`req-${r.id}`}
                    className="flex items-center gap-3 rounded-xl border border-[#E5E5E5] px-4 py-3">
                    <span className="flex-1 text-sm text-[#1C1C1C]">{r.label}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                    <Button
                      onClick={() => uploadForRequest(r.id)}
                      disabled={uploading}
                      variant="outline"
                      data-testid={`req-upload-${r.id}`}
                      className="rounded-full border-[#E5E5E5] hover:border-[#114A31] text-[#1C1C1C] h-8 px-3 text-xs"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> {done ? "Replace" : "Upload"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Documents delivered by our specialists */}
        {intake?.delivered_files?.length > 0 && (
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="delivered-section">
            <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Documents from your specialist</h2>
            <p className="text-sm text-[#595D5A] mt-1">
              Filed forms and prepared documents your specialist has delivered.
            </p>
            <ul className="mt-5 space-y-2" data-testid="delivered-list">
              {intake.delivered_files.map((f) => (
                <li key={f.file_id} className="flex items-center gap-3 text-sm bg-[#E6F0EB] rounded-xl px-4 py-3">
                  <FileText className="h-4 w-4 text-[#114A31]" />
                  <span className="flex-1 text-[#1C1C1C] truncate">{f.original_filename}</span>
                  {f.seen_by_client === false && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#114A31] text-white px-2 py-0.5 rounded-full" data-testid={`delivered-new-${f.file_id}`}>New</span>
                  )}
                  <button
                    onClick={() => downloadDelivered(f.storage_path, f.original_filename, f.file_id)}
                    data-testid={`download-delivered-${f.file_id}`}
                    className="text-[#114A31] hover:text-[#1A6B46]"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Message your specialist */}
        {intake?.case_id && (
          <div className="mt-6" data-testid="client-messages">
            <CaseMessages caseId={intake.case_id} />
          </div>
        )}
      </div>
      <SignFormDialog open={signOpen} onOpenChange={setSignOpen} onSigned={() => { loadSigned(); api.get("/intake/me").then(({ data }) => setIntake(data)).catch(() => {}); }} />
    </div>
  );
}